import { defineStore } from "pinia";

export interface ProductVariant {
  id: string;
  name: string;
  ean: string;
  price: number;
  originalPrice?: number;
  image?: string;
  affiliateUrl: string;
  variantColour?: string | null;
  variantSize?: string | null;
  merchantName?: string;
  inStock: boolean;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number;
  image?: string;
  images?: string[];
  categoryId?: string;
  slug?: string;
  inStock: boolean;
  // Affiliate
  affiliateUrl: string;
  affiliateNetwork?: string;
  merchantName?: string;
  merchantId?: string;
  // Product identifiers
  ean?: string;
  brand?: string;
  currency?: string;
  // Variant grouping
  parentProductId?: string | null;
  variantColour?: string | null;
  variantSize?: string | null;
  _count?: { variants: number };
  variants?: ProductVariant[];
  parentProduct?: (ProductVariant & { variants?: ProductVariant[] }) | null;
}

export const useProductsStore = defineStore("products", () => {
  const products = ref<Product[]>([]);
  const currentProduct = ref<Product | null>(null);
  const total = ref(0);
  const page = ref(1);
  const perPage = 40;
  const loading = ref(false);
  const loadingMore = ref(false);
  // false bis zum ersten abgeschlossenen Fetch der aktuellen Ansicht. Steuert,
  // dass beim Erst-Laden UND beim Navigieren SOFORT Skeletons statt Leer/Empty-
  // State erscheinen (siehe markUnfetched).
  const hasFetched = ref(false);
  const error = ref<string | null>(null);
  const currentCategoryId = ref<string | undefined>();
  const currentSearch = ref<string | undefined>();

  const hasMore = computed(
    () => total.value > 0 && products.value.length < total.value,
  );

  const currentAttributes = ref<Record<string, string[]> | undefined>();

  /** Encodiert attributes für den Query-Param wenn vorhanden + non-empty. */
  const encodeAttributes = (attrs?: Record<string, string[]>) => {
    if (!attrs) return undefined;
    const cleaned: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(attrs)) {
      if (Array.isArray(v) && v.length) cleaned[k] = v;
    }
    return Object.keys(cleaned).length ? JSON.stringify(cleaned) : undefined;
  };

  const fetchProducts = async (params?: {
    categoryId?: string;
    search?: string;
    attributes?: Record<string, string[]>;
  }) => {
    const api = useApi();
    try {
      loading.value = true;
      error.value = null;
      // Reset state before new fetch so hasMore/total are never stale
      products.value = [];
      total.value = 0;
      page.value = 1;
      currentCategoryId.value = params?.categoryId;
      currentSearch.value = params?.search;
      currentAttributes.value = params?.attributes;

      const query: Record<string, any> = { page: 1, perPage };
      if (params?.categoryId) query.categoryId = params.categoryId;
      if (params?.search) query.search = params.search;
      const encodedAttrs = encodeAttributes(params?.attributes);
      if (encodedAttrs) query.attributes = encodedAttrs;

      const response = await api<any>("/api/products", { query });
      products.value = response.products || [];
      total.value = response.total ?? 0;
      page.value = 1;
    } catch (err: any) {
      error.value =
        err.data?.message ||
        err.message ||
        "Produkte konnten nicht geladen werden";
    } finally {
      loading.value = false;
      hasFetched.value = true;
    }
  };

  /**
   * Markiert die aktuelle Liste als "noch nicht (neu) geladen" — beim
   * Navigieren zu einer anderen Kategorie SYNCHRON aufrufen, damit das Grid
   * sofort Skeletons zeigt, bevor der asynchrone Resolve+Fetch startet.
   *
   * Leert zusätzlich die alte Liste sofort: das hasFetched-Flag blendet die
   * Produkte zwar schon per Skeleton-v-if aus, aber das Leeren stellt sicher,
   * dass selbst bei ungünstigem Render-Timing NIE die alten Bilder der
   * vorherigen Seite durchblitzen.
   */
  const markUnfetched = () => {
    hasFetched.value = false;
    products.value = [];
    total.value = 0;
  };

  const fetchMoreProducts = async () => {
    if (loadingMore.value) return;
    if (total.value > 0 && !hasMore.value) return;
    const api = useApi();
    try {
      loadingMore.value = true;
      const nextPage = page.value + 1;
      const query: Record<string, any> = { page: nextPage, perPage };
      if (currentCategoryId.value) query.categoryId = currentCategoryId.value;
      if (currentSearch.value) query.search = currentSearch.value;
      const encodedAttrs = encodeAttributes(currentAttributes.value);
      if (encodedAttrs) query.attributes = encodedAttrs;

      const response = await api<any>("/api/products", { query });
      const newProducts = response.products || [];
      products.value = [...products.value, ...newProducts];
      total.value = response.total || total.value;
      page.value = nextPage;
    } catch (err: any) {
      error.value =
        err.data?.message ||
        err.message ||
        "Produkte konnten nicht geladen werden";
    } finally {
      loadingMore.value = false;
    }
  };

  const fetchProduct = async (id: string) => {
    const api = useApi();
    try {
      loading.value = true;
      error.value = null;
      const response = await api<any>(`/api/products/${id}`);
      currentProduct.value = response.product || response;
    } catch (err: any) {
      error.value =
        err.data?.message ||
        err.message ||
        "Produkt konnte nicht geladen werden";
    } finally {
      loading.value = false;
    }
  };

  return {
    products,
    currentProduct,
    total,
    page,
    perPage,
    loading,
    loadingMore,
    hasFetched,
    hasMore,
    error,
    fetchProducts,
    fetchMoreProducts,
    fetchProduct,
    markUnfetched,
  };
});
